import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

import {
    FiAlertTriangle,
    FiCalendar,
    FiCheckCircle,
    FiClock,
    FiFileText,
    FiFilter,
    FiSearch,
    FiTool,
    FiTruck,
    FiChevronRight
} from 'react-icons/fi';

import { API_BASE_URL } from '../../../config';
import PageHeader from '../../../components/PageHeader/PageHeader';
import LoadingOverlay from '../../../components/LoadingOverlay/LoadingOverlay';

import styles from './Vencimentos.module.css';


const FILTROS = [
    {
        id: 'todos',
        label: 'Todos'
    },
    {
        id: 'prioridade',
        label: 'Com prioridade'
    },
    {
        id: 'vencidos',
        label: 'Vencidos'
    },
    {
        id: 'aet',
        label: 'AET'
    },
    {
        id: 'documentos',
        label: 'Licenças / Documentos'
    },
    {
        id: 'cronotacografo',
        label: 'Cronotacógrafo'
    },
    {
        id: 'manutencao',
        label: 'Manutenção Preventiva'
    }
];


const Vencimentos = () => {

    const [veiculos, setVeiculos] = useState([]);

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState('');

    const [filtro, setFiltro] = useState('todos');

    const [busca, setBusca] = useState('');


    // ============================================================
    // BUSCA DOS DADOS
    // ============================================================

    useEffect(() => {

        const buscarVencimentos = async () => {

            setLoading(true);
            setError('');

            try {

                const token = localStorage.getItem('token');

                const response = await axios.get(
                    `${API_BASE_URL}/api/veiculos/vencimentos`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );

                setVeiculos(response.data || []);

            } catch (err) {

                console.error(
                    'Erro ao carregar vencimentos:',
                    err
                );

                setError(
                    err.response?.data?.error ||
                    'Não foi possível carregar os vencimentos da frota.'
                );

            } finally {

                setLoading(false);

            }
        };


        buscarVencimentos();

    }, []);


    // ============================================================
    // RESUMO
    // ============================================================

    const resumo = useMemo(() => {

        const totalVeiculos = veiculos.length;

        const veiculosComPrioridade =
            veiculos.filter(
                veiculo => veiculo.possuiPrioridade
            ).length;

        const totalVencidos =
            veiculos.reduce(
                (total, veiculo) =>
                    total + (veiculo.vencidos || 0),
                0
            );

        const totalUrgentes =
            veiculos.reduce(
                (total, veiculo) =>
                    total + (veiculo.urgentes || 0),
                0
            );

        return {
            totalVeiculos,
            veiculosComPrioridade,
            totalVencidos,
            totalUrgentes
        };

    }, [veiculos]);


    // ============================================================
    // FILTRO
    // ============================================================

    const veiculosFiltrados = useMemo(() => {

        let resultado = [...veiculos];


        // --------------------------------------------------------
        // BUSCA
        // --------------------------------------------------------

        const termoBusca =
            busca.trim().toLowerCase();

        if (termoBusca) {

            resultado = resultado.filter(veiculo => {

                const textoVeiculo = [
                    veiculo.marca,
                    veiculo.modelo,
                    veiculo.placa,
                    veiculo.chassi,
                    veiculo.tipoVeiculo
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();

                return textoVeiculo.includes(termoBusca);

            });
        }


        // --------------------------------------------------------
        // FILTROS
        // --------------------------------------------------------

        if (filtro === 'prioridade') {

            resultado = resultado.filter(
                veiculo => veiculo.possuiPrioridade
            );

        } else if (filtro === 'vencidos') {

            resultado = resultado.filter(
                veiculo =>
                    veiculo.alertas?.some(
                        alerta =>
                            alerta.status === 'vencido'
                    )
            );

        } else if (filtro !== 'todos') {

            resultado = resultado.filter(
                veiculo =>
                    veiculo.alertas?.some(
                        alerta =>
                            alerta.categoria === filtro
                    )
            );
        }


        return resultado;

    }, [veiculos, filtro, busca]);


    // ============================================================
    // ALERTAS QUE SERÃO EXIBIDOS DENTRO DE CADA VEÍCULO
    // ============================================================

    const filtrarAlertasDoVeiculo = (alertas = []) => {

        if (filtro === 'todos') {
            return alertas;
        }

        if (filtro === 'prioridade') {

            return alertas.filter(
                alerta =>
                    alerta.prioridade > 0
            );
        }

        if (filtro === 'vencidos') {

            return alertas.filter(
                alerta =>
                    alerta.status === 'vencido'
            );
        }

        return alertas.filter(
            alerta =>
                alerta.categoria === filtro
        );
    };


    // ============================================================
    // STATUS
    // ============================================================

    const obterStatusVeiculo = (veiculo) => {

        if ((veiculo.vencidos || 0) > 0) {

            return {
                label: 'Crítico',
                className: styles.statusDanger,
                icon: <FiAlertTriangle />
            };
        }

        if ((veiculo.urgentes || 0) > 0) {

            return {
                label: 'Urgente',
                className: styles.statusUrgent,
                icon: <FiClock />
            };
        }

        if ((veiculo.emAtencao || 0) > 0) {

            return {
                label: 'Atenção',
                className: styles.statusWarning,
                icon: <FiClock />
            };
        }

        return {
            label: 'Em dia',
            className: styles.statusOk,
            icon: <FiCheckCircle />
        };
    };


    const obterClasseAlerta = (status) => {

        switch (status) {

            case 'vencido':
                return styles.alertDanger;

            case 'urgente':
                return styles.alertUrgent;

            case 'atencao':
                return styles.alertWarning;

            default:
                return styles.alertOk;
        }
    };


    const obterIconeCategoria = (categoria) => {

        switch (categoria) {

            case 'manutencao':
                return <FiTool />;

            case 'documentos':
                return <FiFileText />;

            case 'aet':
                return <FiFileText />;

            case 'cronotacografo':
                return <FiClock />;

            default:
                return <FiCalendar />;
        }
    };


    // ============================================================
    // RENDER
    // ============================================================

    return (

        <div className={styles.container}>

            {loading && (
                <LoadingOverlay text="Carregando vencimentos..." />
            )}


            {!loading && (

                <>

                    <PageHeader
                        title="Alertas e Vencimentos"
                        subtitle="Acompanhe documentos, licenças e manutenções da frota."
                        backTo="/sistema-frota"
                    />


                    {/* ================================================= */}
                    {/* RESUMO */}
                    {/* ================================================= */}

                    <div className={styles.summaryGrid}>

                        <div className={styles.summaryCard}>

                            <div className={styles.summaryIcon}>
                                <FiTruck />
                            </div>

                            <div>

                                <span className={styles.summaryLabel}>
                                    Veículos monitorados
                                </span>

                                <strong className={styles.summaryValue}>
                                    {resumo.totalVeiculos}
                                </strong>

                            </div>

                        </div>


                        <div className={styles.summaryCard}>

                            <div className={styles.summaryIconWarning}>
                                <FiAlertTriangle />
                            </div>

                            <div>

                                <span className={styles.summaryLabel}>
                                    Com prioridade
                                </span>

                                <strong className={styles.summaryValue}>
                                    {resumo.veiculosComPrioridade}
                                </strong>

                            </div>

                        </div>


                        <div className={styles.summaryCard}>

                            <div className={styles.summaryIconDanger}>
                                <FiCalendar />
                            </div>

                            <div>

                                <span className={styles.summaryLabel}>
                                    Itens vencidos
                                </span>

                                <strong className={styles.summaryValue}>
                                    {resumo.totalVencidos}
                                </strong>

                            </div>

                        </div>


                        <div className={styles.summaryCard}>

                            <div className={styles.summaryIconUrgent}>
                                <FiClock />
                            </div>

                            <div>

                                <span className={styles.summaryLabel}>
                                    Itens urgentes
                                </span>

                                <strong className={styles.summaryValue}>
                                    {resumo.totalUrgentes}
                                </strong>

                            </div>

                        </div>

                    </div>


                    {/* ================================================= */}
                    {/* FILTROS */}
                    {/* ================================================= */}

                    <div className={styles.filtersCard}>

                        <div className={styles.filtersHeader}>

                            <div className={styles.filtersTitle}>

                                <FiFilter />

                                <span>
                                    Filtros
                                </span>

                            </div>


                            <div className={styles.searchWrapper}>

                                <FiSearch />

                                <input
                                    type="text"
                                    placeholder="Buscar veículo, placa ou chassi..."
                                    value={busca}
                                    onChange={(e) =>
                                        setBusca(e.target.value)
                                    }
                                />

                            </div>

                        </div>


                        <div className={styles.filterButtons}>

                            {FILTROS.map(item => (

                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() =>
                                        setFiltro(item.id)
                                    }
                                    className={
                                        filtro === item.id
                                            ? `${styles.filterButton} ${styles.filterButtonActive}`
                                            : styles.filterButton
                                    }
                                >
                                    {item.label}
                                </button>

                            ))}

                        </div>

                    </div>


                    {/* ================================================= */}
                    {/* ERRO */}
                    {/* ================================================= */}

                    {error && (

                        <div className={styles.errorBox}>

                            <FiAlertTriangle />

                            <span>
                                {error}
                            </span>

                        </div>

                    )}


                    {/* ================================================= */}
                    {/* LISTA */}
                    {/* ================================================= */}

                    {!error && (

                        <div className={styles.listSection}>

                            <div className={styles.listHeader}>

                                <div>

                                    <h2>
                                        Veículos
                                    </h2>

                                    <p>
                                        Os veículos com maior prioridade aparecem primeiro.
                                    </p>

                                </div>


                                <span className={styles.resultCount}>
                                    {veiculosFiltrados.length}{' '}
                                    {veiculosFiltrados.length === 1
                                        ? 'veículo'
                                        : 'veículos'}
                                </span>

                            </div>


                            {veiculosFiltrados.length === 0 ? (

                                <div className={styles.emptyState}>

                                    <FiCheckCircle />

                                    <h3>
                                        Nenhum resultado encontrado
                                    </h3>

                                    <p>
                                        Não existem veículos correspondentes aos filtros selecionados.
                                    </p>

                                </div>

                            ) : (

                                <div className={styles.vehicleList}>

                                    {veiculosFiltrados.map(veiculo => {

                                        const status =
                                            obterStatusVeiculo(
                                                veiculo
                                            );

                                        const alertasVisiveis =
                                            filtrarAlertasDoVeiculo(
                                                veiculo.alertas
                                            );


                                        return (

                                            <div
                                                key={veiculo.id}
                                                className={
                                                    veiculo.possuiPrioridade
                                                        ? `${styles.vehicleCard} ${styles.vehicleCardPriority}`
                                                        : styles.vehicleCard
                                                }
                                            >

                                                {/* HEADER DO VEÍCULO */}

                                                <div className={styles.vehicleHeader}>

                                                    <div className={styles.vehicleIdentity}>

                                                        <div className={styles.vehicleIcon}>
                                                            <FiTruck />
                                                        </div>


                                                        <div>

                                                            <div className={styles.vehicleTitleLine}>

                                                                <h3>
                                                                    {veiculo.marca}{' '}
                                                                    {veiculo.modelo}
                                                                </h3>


                                                                <span
                                                                    className={
                                                                        `${styles.statusBadge} ${status.className}`
                                                                    }
                                                                >
                                                                    {status.icon}

                                                                    {status.label}
                                                                </span>

                                                            </div>


                                                            <div className={styles.vehicleMeta}>

                                                                {veiculo.placa && (
                                                                    <span>
                                                                        Placa:{' '}
                                                                        <strong>
                                                                            {veiculo.placa}
                                                                        </strong>
                                                                    </span>
                                                                )}


                                                                {!veiculo.placa &&
                                                                    veiculo.chassi && (

                                                                    <span>
                                                                        Chassi:{' '}
                                                                        <strong>
                                                                            {veiculo.chassi}
                                                                        </strong>
                                                                    </span>

                                                                )}


                                                                {veiculo.tipoVeiculo && (
                                                                    <span>
                                                                        {veiculo.tipoVeiculo}
                                                                    </span>
                                                                )}

                                                            </div>

                                                        </div>

                                                    </div>


                                                    <Link
                                                        to={`/frota/veiculos/${veiculo.id}`}
                                                        className={styles.vehicleLink}
                                                    >
                                                        Ver veículo

                                                        <FiChevronRight />
                                                    </Link>

                                                </div>


                                                {/* CONTADORES */}

                                                <div className={styles.vehicleStats}>

                                                    <div>

                                                        <span>
                                                            Alertas
                                                        </span>

                                                        <strong>
                                                            {veiculo.quantidadeAlertas || 0}
                                                        </strong>

                                                    </div>


                                                    <div>

                                                        <span>
                                                            Vencidos
                                                        </span>

                                                        <strong>
                                                            {veiculo.vencidos || 0}
                                                        </strong>

                                                    </div>


                                                    <div>

                                                        <span>
                                                            Urgentes
                                                        </span>

                                                        <strong>
                                                            {veiculo.urgentes || 0}
                                                        </strong>

                                                    </div>


                                                    <div>

                                                        <span>
                                                            Atenção
                                                        </span>

                                                        <strong>
                                                            {veiculo.emAtencao || 0}
                                                        </strong>

                                                    </div>

                                                </div>


                                                {/* ALERTAS */}

                                                <div className={styles.alertList}>

                                                    {alertasVisiveis.length === 0 ? (

                                                        <div className={styles.noAlerts}>

                                                            <FiCheckCircle />

                                                            <span>
                                                                Nenhum item para este filtro.
                                                            </span>

                                                        </div>

                                                    ) : (

                                                        alertasVisiveis.map(
                                                            alerta => (

                                                                <div
                                                                    key={alerta.id}
                                                                    className={
                                                                        `${styles.alertItem} ${obterClasseAlerta(alerta.status)}`
                                                                    }
                                                                >

                                                                    <div className={styles.alertCategoryIcon}>
                                                                        {obterIconeCategoria(
                                                                            alerta.categoria
                                                                        )}
                                                                    </div>


                                                                    <div className={styles.alertContent}>

                                                                        <div className={styles.alertTop}>

                                                                            <strong>
                                                                                {alerta.tipo}
                                                                            </strong>


                                                                            <span
                                                                                className={
                                                                                    `${styles.alertStatus} ${obterClasseAlerta(alerta.status)}`
                                                                                }
                                                                            >

                                                                                {alerta.status === 'vencido' &&
                                                                                    'Vencido'}

                                                                                {alerta.status === 'urgente' &&
                                                                                    'Urgente'}

                                                                                {alerta.status === 'atencao' &&
                                                                                    'Atenção'}

                                                                                {alerta.status === 'ok' &&
                                                                                    'Em dia'}

                                                                            </span>

                                                                        </div>


                                                                        <p>
                                                                            {alerta.mensagem}
                                                                        </p>

                                                                    </div>

                                                                </div>

                                                            )
                                                        )

                                                    )}

                                                </div>

                                            </div>

                                        );

                                    })}

                                </div>

                            )}

                        </div>

                    )}

                </>

            )}

        </div>
    );
};


export default Vencimentos;